/**
 * 🎯 Name Collection & AI Consent Component (Web)
 * 2-page welcome flow for Apple App Store compliance:
 *   Page 1: Profile (conditional name fields) + Community opt-in
 *   Page 2: AI Data Consent (required before using the app)
 *
 * Guidelines addressed:
 *   - 5.1.1(i) / 5.1.2(i): AI data disclosure + consent
 *   - Sign in with Apple: Don't re-ask for name/email already provided
 */

import React, { useState } from 'react';
import { useRouter } from 'expo-router';
import { Loader2, Shield } from 'lucide-react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api';
import './NameCollection.css';

interface NameCollectionProps {
  email: string;
  isWaitlistUser?: boolean;
  waitlistData?: {
    first_name?: string;
    last_name?: string;
  };
}

export const NameCollection: React.FC<NameCollectionProps> = ({
  email,
  isWaitlistUser = false,
  waitlistData
}) => {
  const router = useRouter();

  const [currentPage, setCurrentPage] = useState(1);

  // Page 1 state
  const [firstName, setFirstName] = useState(waitlistData?.first_name || '');
  const [lastName, setLastName] = useState(waitlistData?.last_name || '');
  const [emailOptIn, setEmailOptIn] = useState(false);

  // Page 2 state
  const [aiConsent, setAiConsent] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If waitlist/Apple Sign In provided names, don't re-ask (Apple compliance)
  const needsNameFields = !waitlistData?.first_name || !waitlistData?.last_name;

  const isPage1Valid = needsNameFields
    ? firstName.trim().length > 0 && lastName.trim().length > 0
    : true;

  const handleContinueToPage2 = () => {
    if (!isPage1Valid) {
      setError('Please enter both first and last name');
      return;
    }
    setError(null);
    setCurrentPage(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!aiConsent) {
      setError('Please provide your consent to continue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/collect-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await storage.getItem('jwt_token')}`
        },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email_opt_in: emailOptIn,
          ai_data_consent: aiConsent,
          ai_data_consent_version: '1.0',
          source: isWaitlistUser ? 'waitlist_user' : 'new_user'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save information');
      }

      const result = await response.json();
      console.log('✅ Name collection + AI consent successful:', result);

      router.replace('/onboarding');

    } catch (error) {
      console.error('❌ Name collection failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to save information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="name-collection-shell">
      <div className="name-collection-inner">
        {/* Header */}
        <div className="name-collection-header">
          <div className="name-collection-icon" aria-hidden="true">
            <img src="/favicon.png" alt="Coach Cedar" className="name-collection-logo" />
          </div>
          <h1 className="name-collection-title">Welcome to Coach Cedar</h1>
          <p className="name-collection-subtitle">
            {currentPage === 1
              ? (isWaitlistUser
                ? "We're excited to have you! Let's get you set up."
                : "Just a couple of things before we get started.")
              : "One more thing before we begin."
            }
          </p>
        </div>

        {/* Pages container with slide animation */}
        <div className="name-collection-pages-container">
          <div
            className="name-collection-pages-track"
            style={{ transform: `translateX(${currentPage === 1 ? '0%' : '-50%'})` }}
          >
            {/* ===== PAGE 1: Profile & Community ===== */}
            <div className="name-collection-page">
              <div className="name-collection-card">
                <div className="name-collection-card-inner">
                  {/* Conditional Name Fields */}
                  {needsNameFields && (
                    <>
                      <div>
                        <label htmlFor="firstName" className="name-collection-label">
                          First Name *
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          value={firstName}
                          onChange={(e) => {
                            setFirstName(e.target.value);
                            if (error) setError(null);
                          }}
                          className="name-collection-field"
                          placeholder="Enter your first name"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <label htmlFor="lastName" className="name-collection-label">
                          Last Name *
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            if (error) setError(null);
                          }}
                          className="name-collection-field"
                          placeholder="Enter your last name"
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </>
                  )}

                  {/* Community Opt-in */}
                  <div className="name-collection-optin" style={needsNameFields ? { marginTop: 16 } : undefined}>
                    <div className="name-collection-optin-row">
                      <input
                        id="emailOptIn"
                        type="checkbox"
                        checked={emailOptIn}
                        onChange={(e) => setEmailOptIn(e.target.checked)}
                        className="name-collection-checkbox"
                        disabled={isSubmitting}
                      />
                      <div>
                        <label htmlFor="emailOptIn" className="name-collection-optin-title" style={{ cursor: 'pointer' }}>
                          Join the Coach Cedar Community
                        </label>
                        <p className="name-collection-optin-desc">
                          Stay up-to-date with product updates, community news, and stories.
                          We will never share your email.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {currentPage === 1 && error && (
                    <div className="name-collection-error">{error}</div>
                  )}

                  {/* Continue Button */}
                  <button
                    type="button"
                    disabled={!isPage1Valid}
                    className="name-collection-cta"
                    onClick={handleContinueToPage2}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>

            {/* ===== PAGE 2: AI Data Consent ===== */}
            <div className="name-collection-page">
              <form onSubmit={handleSubmit}>
                <div className="name-collection-card">
                  <div className="name-collection-card-inner">
                    <h2 className="name-collection-ai-heading">
                      How Coach Cedar Uses AI With Your Data
                    </h2>

                    <p className="name-collection-ai-body">
                      Coach Cedar uses artificial intelligence from <strong>OpenAI</strong> and <strong>Anthropic</strong> to
                      power your coaching experience, along with custom guidance sourced across
                      1000+ reputable research studies. We send relevant portions of your data to
                      these services during each interaction, including:
                    </p>

                    <ul className="name-collection-ai-bullets">
                      <li><strong>Health &amp; fitness info</strong> — workouts, injuries, sleep, activity levels</li>
                      <li><strong>Profile details</strong> — name, age, gender, fitness goals</li>
                      <li><strong>Conversation history</strong> — your messages and coaching context</li>
                      <li><strong>Coaching data</strong> — training plans, progress, and observations</li>
                    </ul>

                    <p className="name-collection-ai-body">
                      Your data is <strong>not used to train external AI models</strong>. It is temporarily retained for
                      safety monitoring (up to 30 days by OpenAI, up to 7 days by Anthropic), then permanently
                      deleted. No external system builds a persistent profile of you.
                    </p>

                    <p className="name-collection-ai-body">
                      All long-term storage of your data is controlled exclusively by Coach Cedar on our secure
                      infrastructure. For full details, see our{' '}
                      <a
                        href="https://coachcedar.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="name-collection-ai-link"
                      >
                        Privacy Policy
                      </a>.
                    </p>

                    {/* Required Consent Checkbox */}
                    <div className="name-collection-consent-row">
                      <input
                        id="aiConsent"
                        type="checkbox"
                        checked={aiConsent}
                        onChange={(e) => {
                          setAiConsent(e.target.checked);
                          if (error) setError(null);
                        }}
                        className="name-collection-checkbox"
                        disabled={isSubmitting}
                      />
                      <div>
                        <label htmlFor="aiConsent" className="name-collection-consent-label" style={{ cursor: 'pointer' }}>
                          I consent to Coach Cedar sharing my health and fitness data with third-party
                          AI services as described above and in our{' '}
                          <a
                            href="https://coachcedar.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="name-collection-ai-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </a>.
                        </label>
                      </div>
                    </div>

                    {/* Error Message */}
                    {currentPage === 2 && error && (
                      <div className="name-collection-error">{error}</div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={!aiConsent || isSubmitting}
                      className="name-collection-cta"
                    >
                      {isSubmitting ? (
                        <span className="name-collection-cta-inner">
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Saving...
                        </span>
                      ) : (
                        'Go Meet Coach Cedar'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="name-collection-footer">
          <p style={{ margin: 0 }}>
            By continuing, you agree to Coach Cedar's{' '}
            <a
              href="https://coachcedar.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="name-collection-ai-link"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="https://coachcedar.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="name-collection-ai-link"
            >
              Terms of Use
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NameCollection;
